import { Object3D, Vector3 } from 'three';
import { degToRad } from 'three/src/math/MathUtils.js';
import { inject, ref, watch } from 'vue';

import { lerp, randomFloat, randomInt, toRadians } from '@resn/gozer-math';
import { useResize } from '@resn/gozer-vue';
import { gsap } from '@resn/gsap';
import { CustomWiggle } from '@resn/gsap/CustomWiggle';

import { useInteractionMotion } from '~/composables/useInteractionMotion';
import {
    GRAIL_TUNNEL_REVEAL,
    GRAIL_TUNNEL_SET,
    GRAIL_TUNNEL_WIPE,
    RAW_CARD_ASPECT,
} from '~/core/constants';
import { createLogger } from '~/utils/logger';

const logger = createLogger('gl.card-motion');

export const createMotionVectors = () => {
    return { pos: new Vector3(), rot: new Vector3() };
};

export const useCardMotion = ({
    object = null,
    interactive = ref(false),
    motionActive = ref(false),
    onHeartbeatRepeat = () => {},
} = {}) => {
    if (!object) {
        return logger.warn('Required motion object missing', {
            event: 'argument.missing',
            argument: 'object',
            consumer: 'useCardMotion',
        });
    }

    const sceneCamera = inject('sceneCamera', null);
    const cardZoomPr = sceneCamera?.cardZoomPr;
    const viewport = useResize();

    const tweens = { ambientPr: 0, revealPr: 0 };

    const _tempRot = new Vector3();

    const innerObject = new Object3D();
    const flipObject = new Object3D();
    const rotObject = new Object3D();
    const interactionObject = new Object3D();
    const finalObject = flipObject;
    innerObject.name = 'innerObject';
    flipObject.name = 'flipObject';
    rotObject.name = 'rotObject';
    interactionObject.name = 'interactionObject';
    finalObject.name = 'finalObject';

    object.add(innerObject);
    innerObject.add(interactionObject);
    interactionObject.add(rotObject);
    rotObject.add(flipObject);

    const tempMotion = createMotionVectors();
    const rotMotion = createMotionVectors();
    const ambientMotion = createMotionVectors();
    const grailMotion = createMotionVectors();
    const flipMotion = createMotionVectors();
    const outMotion = createMotionVectors();

    const interaction = useInteractionMotion();

    const createWiggleEffect = () => {
        const motion = createMotionVectors();

        const downDur = 0.2;
        const upDur = 0.8;
        const wiggleDur = 1.5;
        const endStart = downDur + wiggleDur - 0.6;
        const downEase = 'power1.inOut';

        CustomWiggle.create('wiggle', { wiggles: 8, type: 'easeOut' });

        const tl = gsap
            .timeline({ paused: true })
            .timeScale(1.5)
            .to(motion.pos, { z: -0.3, duration: downDur, ease: downEase }, 0)
            .to(motion.pos, { z: 0.3, duration: upDur, ease: 'power3.out' }, downDur)
            .to(motion.rot, { z: 0.03, duration: wiggleDur, ease: 'wiggle' }, downDur)
            .addLabel('down')
            .to(motion.pos, { z: 0, duration: 1, ease: 'power2.inOut' }, endStart);

        return { tl, ...motion, type: 'wiggle', defines: { USE_TRANSITION_WIPE: true } };
    };

    const createSpinEffect = () => {
        const motion = createMotionVectors();

        const downDur = 0.2;
        const upDur = 0.8;
        const downEase = 'power1.inOut';
        const endStart = downDur + 0.4;

        const tl = gsap
            .timeline({ paused: true })
            .timeScale(1.5)
            .to(motion.pos, { z: -0.3, duration: downDur, ease: downEase }, 0)
            .to(motion.pos, { z: 0.3, duration: upDur, ease: 'power3.out' }, downDur)
            .to(motion.pos, { z: 0, duration: 1, ease: 'power2.inOut' }, endStart)
            .to(
                motion.rot,
                {
                    y: toRadians(360),
                    duration: 2.3,
                    ease: 'expo.out',
                },
                downDur + 0.05
            );

        return {
            tl,
            ...motion,
            type: 'spin',
            effects: ['vortex'],
            defines: { USE_TRANSITION_WIPE: true },
        };
    };

    const createTiltEffect = () => {
        const motion = createMotionVectors();

        const downDur = 0.2;
        const upDur = 0.8;
        const downEase = 'power1.inOut';
        const pauseDur = 2;
        const endStart = downDur + pauseDur - 0.8;

        const tweens = { rotatePr: 0, strPr: 0 };

        const tl = gsap
            .timeline({
                paused: true,
                onUpdate: () => {
                    const angle = toRadians(lerp(tweens.rotatePr, -90, 90));
                    const angleStr = lerp(tweens.strPr, 0, 0.3);
                    const posStr = lerp(tweens.strPr, 0, 0.075);
                    // const posStr = lerp(tweens.strPr, 0, 0.01);

                    const angleOffset = toRadians(90);
                    motion.rot.x = Math.sin(angle - angleOffset) * angleStr;
                    motion.rot.y = Math.cos(angle - angleOffset) * angleStr;
                    motion.pos.x = Math.sin(angle) * posStr;
                    motion.pos.y = Math.cos(angle) * posStr;
                },
            })
            .timeScale(1.5)

            .to(motion.pos, { z: -0.3, duration: downDur, ease: downEase }, 0)
            .to(motion.pos, { z: 0.3, duration: upDur, ease: 'power3.out' }, downDur)

            .to(tweens, { rotatePr: 1, duration: pauseDur, ease: 'sine.inOut' }, downDur)
            .to(tweens, { strPr: 1, duration: upDur, ease: 'power3.out' }, downDur)

            .to(motion.pos, { z: 0, duration: 1, ease: 'power2.inOut' }, endStart)
            .to(tweens, { strPr: 0, duration: 1, ease: 'power2.inOut' }, endStart);

        return {
            tl,
            ...motion,
            type: 'tilt',
            effects: ['vortex'],
            defines: { USE_TRANSITION_WIPE: true },
        };
    };

    const createBounceEffect = () => {
        const downDur = 0.2;
        const upDur = 1;

        const motion = createMotionVectors();
        const tl = gsap
            .timeline({ paused: true })

            .to(motion.pos, { z: 0.05, duration: downDur, ease: 'power1.inOut' })
            .to(motion.pos, { z: 0, duration: upDur, ease: 'power3.out' });
        // .to(motion.pos, { z: 0, duration: 1, ease: 'power2.inOut' });

        return {
            tl,
            ...motion,
            type: 'bounce',
        };
    };

    const easeInOutQuad = (t) => {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    };

    const createZoomEffect = () => {
        const motion = createMotionVectors();

        const zoomDur = 0.5;
        const zoomRange = 1;
        const scanRange = 0.2;
        const tweens = { progress: 0, tilt: 0 };
        const tl = gsap
            .timeline({
                paused: true,
                onUpdate: () => {
                    const p = Math.min(1, tweens.progress * 2);
                    const halfProgress = Math.sin(p * Math.PI);
                    motion.rot.x = Math.sin(p * Math.PI * 2) * halfProgress * -0.9 + tweens.tilt;
                    motion.rot.y = Math.cos(p * Math.PI * 2) * halfProgress * 0.2;

                    const rotationStart = 0.0;
                    const rotationEnd = 0.4;
                    const rotationRange = rotationEnd - rotationStart;
                    const rotationProgress = Math.max(
                        0,
                        Math.min(1, (tweens.progress - rotationStart) / rotationRange)
                    );

                    // easeINout:
                    const easeProgress = easeInOutQuad(rotationProgress);

                    motion.rot.y += easeProgress * Math.PI * 2;

                    motion.rot.x += halfProgress * -0.4;
                    motion.rot.y += halfProgress * -0.4;
                },
            })
            .to(tweens, { progress: 1, duration: 2, ease: 'linear' })
            .to(motion.pos, { z: zoomRange, duration: zoomDur, ease: 'quad.inOut' }, 0)
            .to(motion.pos, { z: -0.0, duration: zoomDur + 0.25, ease: 'quad.inOut' }, 1 - zoomDur)
            // .to(motion.pos, { z: 0, duration: 0.25, ease: 'sine.inOut' }, 1.0)

            .to(motion.pos, { y: scanRange, duration: 0.33, ease: 'sine.inOut' }, 0)
            .to(motion.pos, { y: -scanRange, duration: 0.33, ease: 'sine.inOut' }, 0.33)
            .to(motion.pos, { y: 0, duration: 0.33, ease: 'sine.inOut' }, 0.66)
            .to(motion.pos, { y: 0, duration: 0.33, ease: 'sine.inOut' }, 0.66)
            .to(tweens, { tilt: -0.5, duration: 0.25, ease: 'sine.inOut' }, 0.5)
            .to(tweens, { tilt: 0, duration: 0.5, ease: 'sine.inOut' }, 0.75)
            // .to(tweens, { tilt: 0, duration: 0.25, ease: 'sine.inOut' }, 1)
            .timeScale(0.75);
        // .to(motion.rot, { x: -0.7, y: 0.4, z: 0.0, duration: 0.33, ease: 'quad.inOut' }, 0)
        // .to(motion.rot, { x: 0.7, y: -0.3, z: 0.0, duration: 0.33, ease: 'quad.inOut' }, 0.33)

        // .to(motion.rot, { x: 0, y: 0, z: 0, duration: zoomDur, ease: 'quad.inOut' }, 0.66);

        return {
            tl,
            ...motion,
            type: 'zoom',
            revealDuration: 2,
            effects: ['shine'],
            defines: { USE_TRANSITION_SHINE: false, USE_TRANSITION_WIPE: true },
        };
    };
    const createBurnEffect = () => {
        const motion = createMotionVectors();

        const tweens = { progress: 0, tilt: 0 };
        const tl = gsap
            .timeline({
                paused: true,
            })
            .to(tweens, { progress: 1, duration: 2, ease: 'linear' })
            .to(motion.rot, { y: Math.PI * 1.5, duration: 0.75, ease: 'quad.in' }, 0)
            .to(motion.rot, { y: Math.PI * 2, duration: 2, ease: 'elastic.out' }, 0.75)
            .to(motion.rot, { z: 0.25, duration: 0.5, ease: 'quad.inOut' }, 0)
            .to(motion.rot, { z: 0, duration: 0.5, ease: 'quad.inOut' }, 0.5)

            .to(motion.pos, { z: -0.4, duration: 0.25, ease: 'sine.inOut' }, 0)
            .to(motion.pos, { z: 0.4, duration: 0.25, ease: 'sine.inOut' }, 0.25)
            .to(motion.pos, { z: 0, duration: 1, ease: 'elastic.out' }, 0.5)

            .timeScale(0.75);

        return {
            tl,
            ...motion,
            type: 'burn',
            revealDuration: 1.5,
            effects: ['vortex'],
            defines: { USE_TRANSITION_BURN: true },
        };
    };

    const createHeartbeatEffect = () => {
        const motion = createMotionVectors();

        const tl = gsap
            .timeline({
                paused: true,
                repeat: -1,
                repeatDelay: 0.8,
                onStart: onHeartbeatRepeat,
                onRepeat: () => {
                    if (heartbeatEffect.active) {
                        onHeartbeatRepeat();
                    } else {
                        tl.pause();
                    }
                },
            })
            .to(motion.pos, { z: 0.25, duration: 0.2, ease: 'power1.out' })
            .to(motion.pos, { z: 0, duration: 0.8, ease: 'power1.out' });

        return { tl, ...motion };
    };

    const createChaseEffect = () => {
        const motion = createMotionVectors();

        const dirx = Math.sign(randomFloat(-1, 1));
        const diry = Math.sign(randomFloat(-1, 1));
        const rotStart = { x: 0.2 * dirx, y: 0.2 * diry };

        const tl = gsap
            .timeline({
                paused: true,
            })
            .addLabel('start')
            .addLabel('middle', 1)
            .addLabel('end', 2)

            .to(motion.pos, { z: -0.5, duration: 0.1, ease: 'sine.in' }, 'start')
            .to(motion.pos, { z: 0.5, duration: 0.8, ease: 'power2.out' }, 'start+=0.1')
            .to(motion.rot, { ...rotStart, duration: 0.8, ease: 'power2.out' }, 'start+=0.1')

            .to(motion.rot, { x: 0, duration: 1, ease: 'linear' }, 'middle')
            .to(motion.pos, { z: -1, duration: 2, ease: 'linear' }, 'middle')
            .to(motion.rot, { y: -Math.PI, duration: 1, ease: 'back.out(2)' }, 'middle')

            .to(motion.rot, { y: -Math.PI * 1.2, duration: 0.2, ease: 'sine.in' }, 'end')
            .to(motion.rot, { y: 0, duration: 1, ease: 'back.out(1.2)' }, 'end+=0.3')
            .to(motion.pos, { z: 0, duration: 1, ease: 'back.out(1.2)' }, 'end+=0.3');

        return { tl, ...motion };
    };

    const createGrailTunnelEffect = () => {
        const tweens = { glidePr: 0 };

        const motion = createMotionVectors();
        const glideMotion = createMotionVectors();
        const finalMotion = createMotionVectors();

        const tl = gsap
            .timeline({
                paused: true,
                onUpdate: () => {
                    finalMotion.pos
                        .copy(glideMotion.pos)
                        .multiplyScalar(tweens.glidePr)
                        .add(motion.pos);
                    finalMotion.rot
                        .copy(glideMotion.rot)
                        .multiplyScalar(tweens.glidePr)
                        .add(motion.rot);
                },
            })
            .addLabel('setup', GRAIL_TUNNEL_SET)
            .addLabel('start', GRAIL_TUNNEL_REVEAL)
            .addLabel('reveal', GRAIL_TUNNEL_WIPE)

            // Init values
            .set(motion.pos, { y: 0.5, z: -5 }, 'setup')
            .set(motion.rot, { x: toRadians(110) }, 'setup')

            // Start animation
            .to(motion.pos, { y: 0, z: 0, duration: 1.5, ease: 'power2.out' }, 'start')
            .to(motion.rot, { x: toRadians(100), duration: 0.75, ease: 'power2.out' }, 'start')
            .to(motion.rot, { x: toRadians(90), duration: 0.75, ease: 'sine.inOut' }, 'start+=0.75')
            .fromTo(
                motion.rot,
                { y: toRadians(360) },
                { y: 0, duration: 1.5, ease: 'power2.inOut' },
                'start+=0.375'
            );

        // Glide animation
        const lrDur = 0.75;
        const lrRepeat = 8;
        const lrDist = 0.05;
        const lrRot = 0.1;
        tl.to(tweens, { glidePr: 1, duration: 1.5, ease: 'sine.inOut' }, 'start+=0.75')
            .to(tweens, { glidePr: 0, duration: 1.5, ease: 'sine.inOut' }, `reveal`)
            .fromTo(
                glideMotion.pos,
                { x: -lrDist },
                {
                    x: lrDist,
                    duration: lrDur,
                    ease: 'sine.inOut',
                    repeat: lrRepeat,
                    yoyo: true,
                },
                'start+=0.6'
            )
            .fromTo(
                glideMotion.rot,
                { y: -lrRot },
                {
                    y: lrRot,
                    duration: lrDur,
                    ease: 'sine.inOut',
                    repeat: lrRepeat,
                    yoyo: true,
                },
                'start+=0.6'
            )

            // Rotation animation
            // rotation when deg 0 = z: 0.4, x: 0
            // rotation when deg 90 = z: 0, x: -0.4
            // rotation when deg 180 = z: -0.4, x: 0
            .fromTo(
                glideMotion.rot,
                { z: 0.3 },
                {
                    z: -0.3,
                    duration: 3,
                    ease: 'sine.inOut',
                    repeat: 2,
                    yoyo: true,
                },
                0
            )
            .fromTo(
                glideMotion.rot,
                { x: -0.3 },
                {
                    x: 0.3,
                    duration: 3,
                    ease: 'sine.inOut',
                    repeat: 2,
                    yoyo: true,
                },
                1.5
            );

        // Reveal animation
        const dur = 0.6;
        tl.to(motion.pos, { z: -0.3, duration: dur, ease: 'power2.inOut' }, 'reveal');
        // tl.to(
        //     motion.rot,
        //     { x: toRadians(110), y: -0.1, duration: dur, ease: 'power2.inOut' },
        //     'reveal'
        // );

        const dur2 = 0.6;
        tl.to(motion.pos, { z: 0.5, duration: dur2, ease: 'power2.inOut' }, `reveal+=${dur}`);
        tl.to(
            motion.rot,
            { x: 0, y: 0, z: 0, duration: dur2, ease: 'power1.inOut' },
            `reveal+=${dur}`
        );
        // tl.to(
        //     motion.rot,
        //     { x: -0.1, y: 0.1, duration: dur2, ease: 'power2.inOut' },
        //     `reveal+=${dur}`
        // );

        const dur3 = 0.9;
        // tl.to(
        //     motion.rot,
        //     { x: 0, y: 0, duration: dur3, ease: 'power1.inOut' },
        //     `reveal+=${dur + dur2}`
        // );
        tl.to(motion.pos, { z: 0, duration: dur3, ease: 'power1.inOut' }, `reveal+=${dur + dur2}`);

        // tl.to(
        //     motion.pos,
        //     { x: 0, y: 0, z: 0, duration: 0.5, ease: 'power1.inOut' },
        //     GRAIL_TUNNEL_WIPE
        // );
        // tl.to(
        //     motion.rot,
        //     { x: 0, y: 0, z: 0, duration: 0.5, ease: 'power1.inOut' },
        //     GRAIL_TUNNEL_WIPE
        // );

        return { tl, ...finalMotion };
    };

    const createSuperFractorEffect = () => {
        const motion = createMotionVectors();

        CustomWiggle.create('superwiggle', { wiggles: 35, type: 'easeInOut' });
        const tl = gsap.timeline({ paused: true });

        tl.to(motion.pos, { z: -0.6, duration: 1.8, ease: 'power1.in' });
        tl.to(motion.pos, { z: 0.3, duration: 2, ease: 'elastic.out(1.5, 0.5)' });
        tl.to(
            motion.rot,
            {
                y: toRadians(360),
                duration: 1.6,
                ease: 'expo.out',
            },
            '<'
        );

        // tl.to(motion.rot, { x: -0.73, y: -0.55, z: -0.48, duration: 0.6, ease: 'power2.out' }, '<');
        tl.to(motion.pos, { z: 0, duration: 1.5, ease: 'elastic.out(1.2, 0.3)' }, '+=1');
        // tl.to(motion.pos, { z: 0, duration: 0.6, ease: 'back.inOut(5)' }, '+=1');

        return { tl, ...motion, type: 'super-fractor' };
    };

    const heartbeatEffect = createHeartbeatEffect();

    // Sweat effects
    const wiggleEffect = createWiggleEffect();
    const spinEffect = createSpinEffect();
    const tiltEffect = createTiltEffect();
    const bounceEffect = createBounceEffect();
    const zoomEffect = createZoomEffect();
    const burnEffect = createBurnEffect();
    const chaseEffect = createChaseEffect();
    const grailTunnelEffect = createGrailTunnelEffect();
    const superFractorEffect = createSuperFractorEffect();

    const sweatEffects = [
        wiggleEffect,
        spinEffect,
        tiltEffect,
        zoomEffect,
        burnEffect,
        bounceEffect,
    ];

    const update = (timestamp) => {
        const speed = timestamp * 0.0005;

        if (tweens.ambientPr > 0) {
            ambientMotion.rot.x = Math.sin(speed) * 0.15 * tweens.ambientPr;
            ambientMotion.rot.y = Math.cos(speed) * 0.15 * tweens.ambientPr;
        }

        interaction.update();

        if (cardZoomPr) {
            cardZoomPr.value = interaction.zoomPr.value;
        }

        // // Inner Object
        tempMotion.pos
            // .copy(ambientMotion.pos)
            .copy(interaction.pressMotion)
            .add(wiggleEffect.pos)
            .add(spinEffect.pos)
            .add(tiltEffect.pos)
            .add(zoomEffect.pos)
            .add(burnEffect.pos)
            .add(bounceEffect.pos)
            .add(grailMotion.pos)
            .add(chaseEffect.pos)
            .add(grailTunnelEffect.pos)
            .add(superFractorEffect.pos)
            .add(outMotion.pos)
            .add(heartbeatEffect.pos);
        tempMotion.rot
            // .copy(ambientMotion.rot)
            .copy(wiggleEffect.rot)
            .add(spinEffect.rot)
            .add(tiltEffect.rot)
            .add(bounceEffect.rot)
            .add(zoomEffect.rot)
            .add(burnEffect.rot)
            .add(grailMotion.rot)
            .add(chaseEffect.rot)
            .add(grailTunnelEffect.rot)
            .add(superFractorEffect.rot)
            .add(outMotion.rot)
            .add(heartbeatEffect.rot);

        innerObject.position.copy(tempMotion.pos);
        innerObject.rotation.setFromVector3(tempMotion.rot);

        interactionObject.position.copy(interaction.motion.pos);
        interactionObject.quaternion.copy(interaction.motion.final);

        flipObject.position.copy(flipMotion.pos);
        flipObject.rotation.setFromVector3(flipMotion.rot);

        rotObject.position.copy(rotMotion.pos).add(ambientMotion.pos);
        rotObject.rotation.setFromVector3(_tempRot.copy(rotMotion.rot).add(ambientMotion.rot));
    };

    const setFlip = (val = false, force = false) => {
        const _rotation = val ? degToRad(180) : 0;

        const duration = force ? 0 : 0.5;
        gsap.killTweensOf(flipMotion.rot, 'y');
        gsap.to(flipMotion.rot, { y: _rotation, duration, ease: 'power3.out' });

        interaction.reset();
    };

    const setRotation = (val = 0, force = false) => {
        const duration = force ? 0 : 0.5;
        const scale = val % 180 !== 0 && viewport.aspect < 1 ? 0.85 : 1;

        gsap.killTweensOf(rotMotion.rot, 'z');
        gsap.to(rotMotion.rot, { z: degToRad(-val), duration, ease: 'power3.out' });

        gsap.killTweensOf(rotObject.scale);
        gsap.to(rotObject.scale, { x: scale, y: scale, z: scale, duration, ease: 'power3.out' });
    };

    const playEffect = (type = null) => {
        return playSweatEffect(type);
    };

    const playChaseEffect = () => {
        chaseEffect.tl.progress(0).play();
    };

    const playGrailTunnelEffect = () => {
        grailTunnelEffect.tl.progress(0).play();
    };

    const playSuperFractorEffect = () => {
        superFractorEffect.tl.progress(0).play();
    };

    const playSweatEffect = (type = null) => {
        const effects = sweatEffects;

        const effect = type
            ? sweatEffects.find((effect) => effect.type === type)
            : effects[randomInt(0, effects.length - 1)];
        effect.tl.progress(0).play();

        return effect;
    };

    const playHeartbeatEffect = () => {
        heartbeatEffect.active = true;
        heartbeatEffect.tl.progress(0).play();
    };

    const stopHeartbeatEffect = () => {
        heartbeatEffect.active = false;
    };

    let tlLightsDownReveal;
    const startLightsDownReveal = ({ setDelay = 0, revealDelay = 0 } = {}) => {
        gsap.killTweensOf(tweens);

        tlLightsDownReveal = gsap
            .timeline()

            .set(tweens, { ambientPr: 0 }, setDelay)
            .set(grailMotion.pos, { y: 1.2, z: -5 }, setDelay)
            .set(grailMotion.rot, { x: toRadians(-90), y: toRadians(-360) }, setDelay)

            .to(grailMotion.pos, { y: 0, z: 0, duration: 4, ease: 'power3.out' }, revealDelay)
            .to(grailMotion.rot, { x: 0, duration: 5, ease: 'power3.out' }, revealDelay)
            .to(grailMotion.rot, { y: 0, duration: 3, ease: 'power3.inOut' }, revealDelay + 0.5)
            .to(tweens, { ambientPr: 1, duration: 3, ease: 'power1.inOut' }, revealDelay + 2)
        return tlLightsDownReveal;
    };

    const setMotionActive = (val) => {
        const duration = val ? 3 : 0.5;
        const ambientPr = val ? 1 : 0;

        gsap.killTweensOf(tweens, 'ambientPr');
        gsap.to(tweens, { ambientPr, duration, ease: 'power1.inOut' });
    };

    const setInteractive = (val) => {
        if (val) interaction.enable();
        else interaction.disable();
    };

    const hide = () => {
        gsap.to(outMotion.rot, { x: -0.6, y: 0.6, duration: 1, ease: 'power2.inOut' });
    };

    const reset = () => {
        // Reset out motion
        gsap.killTweensOf(rotMotion.pos);
        gsap.killTweensOf(outMotion.rot);

        tlLightsDownReveal?.kill(); // Reset lights down reveal

        grailMotion.pos.set(0, 0, 0);
        grailMotion.rot.set(0, 0, 0);

        outMotion.rot.set(0, 0, 0);
        rotMotion.pos.set(0, 0, 0);
    };

    watch(motionActive, setMotionActive);
    watch(interactive, setInteractive);

    return {
        innerObject,
        interactionObject,
        rotObject,
        flipObject,
        finalObject,

        update,
        hide,
        reset,
        setFlip,
        setRotation,
        playEffect,
        playChaseEffect,
        playGrailTunnelEffect,
        playSuperFractorEffect,
        playSweatEffect,
        playHeartbeatEffect,
        stopHeartbeatEffect,
        startLightsDownReveal,
        setZoom: interaction.setZoom,
        setZoomManual: interaction.setZoomManual,
        isZooming: interaction.isZooming,
        zoomPr: interaction.zoomPr,
        resetInteractionMotion: interaction.reset,
        isFlipped: interaction.isFlipped,
        interactionMotion: interaction.motion,
    };
};
